/**
 * KPI Data Detail Modal - Generic
 * Shows underlying data rows when any KPI card is clicked.
 * Accepts configurable columns so it works for Orders, Fuel, Drivers, GPS etc.
 */

import { X, Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download, TrendingUp, BarChart3 } from 'lucide-react';
import { Fragment, useState, useMemo, useEffect, ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { formatControlCentreDateTime, type TimeBasis } from '../../utils/formatControlCentreDateTime';

export interface ColumnDef {
  key: string;           // dot-path into the row object, e.g. "pickup.name"
  header: string;        // column header label
  align?: 'left' | 'right';
  render?: (value: any, row: any) => ReactNode;  // custom cell renderer
  /** When false, header is not clickable for sort (default true). */
  sortable?: boolean;
  /** Optional sort value extractor (defaults to column key). */
  sortValue?: (row: any) => string | number | null | undefined;
  /**
   * How to read timestamp values in this column. Default `instant` (shown in UK
   * time). Any column carrying raw Aquarius timestamps MUST set `supplied` (or
   * pre-format with a `render`): their `Z` label is not real, and `instant`
   * would show them an hour late in BST.
   */
  timeBasis?: TimeBasis;
}

interface KPIDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  data: any[];
  columns: ColumnDef[];
  filterFn?: (row: any) => boolean;   // pre-filter rows (e.g. only cancelled)
  searchKeys?: string[];              // dot-paths to search across
  exportFilename?: string;
  isLoading?: boolean;
  /** Shown below the table (e.g. data provenance). */
  footerNote?: string;
  /**
   * Body shown under a row when it is clicked; null for rows that do not
   * expand. Expanding rows get a chevron before the first cell.
   */
  renderExpandedRow?: (row: any) => ReactNode | null;
  /** Stable id for a row, so an open row stays open when the data refreshes (default: the row object). */
  rowKey?: (row: any) => string;
  /**
   * When set and `data` is empty (and user is not searching), replaces the generic “No Data” block.
   * Used e.g. when the UK Trade Tariff returns no rows for an 8-digit code.
   */
  emptyStateMessage?: string;
  /** Optional actions below the table (e.g. primary CTA for data-heavy modals). */
  footerActions?: ReactNode;
  /** Initial sort; with column.sortable false this stays fixed. */
  defaultSort?: { key: string; direction?: 'asc' | 'desc' };
  /** When false, no column headers are clickable for sort (default true). */
  sortable?: boolean;
}

/** Resolve a dot-path like "pickup.name" on an object */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/** Format cell/CSV values; ISO timestamps become "YYYY-MM-DD HH:mm:ss" in UK time. */
function formatCellDisplay(value: unknown, timeBasis?: TimeBasis): string {
  if (value == null || value === '') return '-';
  const asString = String(value);
  const formatted = formatControlCentreDateTime(value, timeBasis);
  return formatted || asString;
}

function compareSortValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  const aNum = typeof a === 'number' ? a : Number(a);
  const bNum = typeof b === 'number' ? b : Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum) && String(a).trim() !== '' && String(b).trim() !== '') {
    // Prefer numeric compare when both look numeric, but keep ISO dates as strings when both parse as dates.
    const aStr = String(a);
    const bStr = String(b);
    const aDate = Date.parse(aStr);
    const bDate = Date.parse(bStr);
    if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && /[-/T:]/.test(aStr) && /[-/T:]/.test(bStr)) {
      return aDate - bDate;
    }
    if (!Number.isNaN(Number(a)) && !Number.isNaN(Number(b)) && /^-?\d+(\.\d+)?$/.test(aStr) && /^-?\d+(\.\d+)?$/.test(bStr)) {
      return aNum - bNum;
    }
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function KPIDataModal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: HeaderIcon = BarChart3,
  data,
  columns,
  filterFn,
  searchKeys,
  exportFilename,
  isLoading = false,
  footerNote,
  emptyStateMessage,
  footerActions,
  defaultSort,
  sortable = true,
  renderExpandedRow,
  rowKey,
}: KPIDataModalProps) {
  const [expandedRow, setExpandedRow] = useState<unknown>(null);
  const keyOf = (row: any): unknown => (rowKey ? rowKey(row) : row);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<string | null>(defaultSort?.key ?? null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSort?.direction ?? 'asc');
  const rowsPerPage = 20;

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setCurrentPage(1);
    setSortKey(defaultSort?.key ?? null);
    setSortDirection(defaultSort?.direction ?? 'asc');
    setExpandedRow(null);
  }, [isOpen, title, defaultSort?.key, defaultSort?.direction]);

  const filteredData = useMemo(() => {
    let rows = data || [];

    // Apply pre-filter
    if (filterFn) {
      rows = rows.filter(filterFn);
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const keys = searchKeys || columns.map(c => c.key);
      rows = rows.filter((row: any) =>
        keys.some(k => {
          const val = getNestedValue(row, k);
          return val != null && String(val).toLowerCase().includes(query);
        })
      );
    }

    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      const sorted = [...rows];
      sorted.sort((ra, rb) => {
        const va = col?.sortValue ? col.sortValue(ra) : getNestedValue(ra, sortKey);
        const vb = col?.sortValue ? col.sortValue(rb) : getNestedValue(rb, sortKey);
        const cmp = compareSortValues(va, vb);
        return sortDirection === 'asc' ? cmp : -cmp;
      });
      rows = sorted;
    }

    return rows;
  }, [data, filterFn, searchQuery, searchKeys, columns, sortKey, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const toggleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    if (!filteredData || filteredData.length === 0) return;

    const headers = columns.map(c => c.header);
    const rows = filteredData.map((row: any) =>
      columns.map(c => {
        const val = getNestedValue(row, c.key);
        const display = formatCellDisplay(val, c.timeBasis).replace(/,/g, '');
        return display === '-' ? '' : display;
      }),
    );

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename || `kpi_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const showCustomEmpty =
    !isLoading && Boolean(emptyStateMessage) && (data?.length ?? 0) === 0 && !searchQuery;
  const showDataControls = !isLoading && ((data?.length ?? 0) > 0 || !emptyStateMessage);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-popover rounded-2xl border border-border shadow-2xl w-full max-w-7xl max-h-screen-90 flex flex-col mx-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <HeaderIcon className="w-7 h-7 text-chart-5" />
              {title}
            </h2>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg transition-colors">
            <X className="w-6 h-6 text-muted-foreground" />
          </button>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-muted-foreground">
            <div>Loading...</div>
            {footerNote ? <p className="text-xs text-muted-foreground mt-6 max-w-xl mx-auto">{footerNote}</p> : null}
          </div>
        ) : (
        <>
        {/* Controls */}
        {showDataControls ? (
        <div className="p-4 border-b border-border bg-card/50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-background rounded-lg border border-border">
                <TrendingUp className="w-4 h-4 text-chart-5" />
                <span className="text-sm font-medium text-white">
                  {filteredData.length.toLocaleString()} row{filteredData.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-chart-5 hover:bg-chart-5 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Export CSV</span>
            </button>
          </div>
        </div>
        ) : null}

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
          {showCustomEmpty ? (
            <div className="text-center py-14 px-6">
              <p className="text-base text-foreground max-w-2xl mx-auto leading-relaxed whitespace-pre-line">{emptyStateMessage}</p>
            </div>
          ) : paginatedData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-card border-b border-border">
                  <tr>
                    {columns.map((col, colIdx) => {
                      const canSort = sortable && col.sortable !== false;
                      const isActive = sortKey === col.key;
                      return (
                        <th
                          key={`${colIdx}-${col.header}-${col.key}`}
                          className={`py-3 px-4 text-sm font-semibold tracking-wide ${
                            col.align === 'right' ? 'text-right' : 'text-left'
                          } ${canSort ? 'cursor-pointer select-none hover:text-white' : ''} ${
                            isActive ? 'text-white' : 'text-foreground'
                          }`}
                          onClick={canSort ? () => toggleSort(col.key) : undefined}
                          aria-sort={
                            isActive
                              ? sortDirection === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                          }
                        >
                          <span className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'justify-end w-full' : ''}`}>
                            {col.header}
                            {canSort && isActive ? (
                              sortDirection === 'asc'
                                ? <ChevronUp className="w-3.5 h-3.5 text-chart-5 shrink-0" aria-hidden />
                                : <ChevronDown className="w-3.5 h-3.5 text-chart-5 shrink-0" aria-hidden />
                            ) : null}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedData.map((row: any, idx: number) => {
                    const expandedBody = renderExpandedRow ? renderExpandedRow(row) : null;
                    const expands = expandedBody != null;
                    const isOpenRow = expands && expandedRow === keyOf(row);
                    return (
                      <Fragment key={idx}>
                        <tr
                          className={`hover:bg-card/50 transition-colors ${expands ? 'cursor-pointer' : ''}`}
                          onClick={expands ? () => setExpandedRow(isOpenRow ? null : keyOf(row)) : undefined}
                          // Reachable from the keyboard like the driver table's rows.
                          tabIndex={expands ? 0 : undefined}
                          onKeyDown={
                            expands
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setExpandedRow(isOpenRow ? null : keyOf(row));
                                  }
                                }
                              : undefined
                          }
                          aria-expanded={expands ? isOpenRow : undefined}
                          title={expands ? (isOpenRow ? 'Click to hide details' : 'Click to see details') : undefined}
                        >
                          {columns.map((col, colIdx) => {
                            const val = getNestedValue(row, col.key);
                            return (
                              <td
                                key={`${colIdx}-${col.header}-${col.key}`}
                                className={`py-3 px-4 text-sm text-foreground ${col.align === 'right' ? 'text-right' : ''}`}
                              >
                                {colIdx === 0 && expands ? (
                                  <span className="inline-flex items-center gap-1">
                                    <ChevronRight
                                      className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${isOpenRow ? 'rotate-90' : ''}`}
                                      aria-hidden
                                    />
                                    {col.render ? col.render(val, row) : formatCellDisplay(val, col.timeBasis)}
                                  </span>
                                ) : col.render ? col.render(val, row) : formatCellDisplay(val, col.timeBasis)}
                              </td>
                            );
                          })}
                        </tr>
                        {isOpenRow ? (
                          <tr>
                            <td colSpan={columns.length} className="px-4 pb-3 pt-0">
                              {expandedBody}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">No Data Found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'Try adjusting your search criteria' : 'No data available for this filter'}
              </p>
            </div>
          )}
          {footerNote ? (
            <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border">{footerNote}</p>
          ) : null}
        </div>

        {footerActions ? (
          <div className="px-6 py-3 border-t border-border bg-background/95 flex flex-wrap items-center justify-end gap-2 shrink-0">
            {footerActions}
          </div>
        ) : null}

        {/* Pagination */}
        {showDataControls && totalPages > 1 && (
          <div className="p-4 border-t border-border bg-card/50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * rowsPerPage + 1} - {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 bg-secondary hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-muted-foreground" />
                </button>
                <span className="px-4 py-2 bg-card rounded-lg text-sm text-white font-medium">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-secondary hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
